import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import StatusPill, { CASE_STATUS_PILL } from '../components/StatusPill';
import { Chevron, Padlock } from '../components/AppIcons';
import { useActiveCompany } from '../state';
import EditEmployeeModal from '../components/EditEmployeeModal';
import EntityChecklistSection from '../components/EntityChecklistSection';
import EntityQuestionGate from '../components/EntityQuestionGate';
import { updateEmployee, type ContractType } from '../../lib/entities/employees';
import type { Employee, HrCase } from '../types';
import '../../styles/app-employees.css';

/* ------------------------------------------------------------- Detail view */

/** Display "Full-time" → backend enum, so an edit round-trips the contract type. */
const CONTRACT_TYPE_TO_DTO: Record<string, ContractType> = {
  'Full-time': 'fullTime',
  'Part-time': 'partTime',
  Casual: 'casual',
};

/** Today as "yyyy-MM-dd" for the offboard endedOn. */
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function EmployeeDetail({ employee, cases }: { employee: Employee; cases: HrCase[] }) {
  const { refetchEmployees } = useActiveCompany();
  const [offboarding, setOffboarding] = useState(false);
  const [offboardError, setOffboardError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  // Answering a per-hire question changes the resolved tiers, so bump this to re-resolve the section.
  const [checklistReload, setChecklistReload] = useState(0);
  const first = employee.name.split(' ')[0];

  const handleOffboard = async () => {
    if (offboarding) return;
    setOffboarding(true);
    setOffboardError(null);
    try {
      await updateEmployee(employee.id, {
        name: employee.name,
        role: employee.role || undefined,
        contractType: CONTRACT_TYPE_TO_DTO[employee.contractType] ?? 'fullTime',
        note: employee.note,
        endedOn: today(),
      });
      await refetchEmployees();
    } catch (err) {
      setOffboardError(err instanceof Error ? err.message : 'Could not offboard right now.');
    } finally {
      setOffboarding(false);
    }
  };

  return (
    <div className="apage">
      <Link to="/app/employees" className="emp-back">
        ← All employees
      </Link>
      <PageHeader
        eyebrow="Employee"
        title={employee.name}
        lead={`${employee.role} · ${employee.contractType} · ${employee.started}`}
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {employee.note && <span className="emp-note">{employee.note}</span>}
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setEditing(true)}
            >
              Edit
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={handleOffboard}
              disabled={offboarding}
            >
              {offboarding ? 'Offboarding…' : 'Offboard'}
            </button>
          </div>
        }
      />
      {offboardError && (
        <p style={{ margin: '0 0 12px', color: 'var(--wine)', fontSize: '13.5px' }} role="alert">
          {offboardError}
        </p>
      )}

      {editing && (
        <EditEmployeeModal
          employeeId={employee.id}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void refetchEmployees();
            // An answer may have moved an obligation's tier, so re-resolve the checklist.
            setChecklistReload((n) => n + 1);
          }}
        />
      )}

      {/* Force any per-hire predicate left unanswered (e.g. capture abandoned) to be completed on load. */}
      <EntityQuestionGate
        base="/api/employees"
        id={employee.id}
        lead={
          <>
            Before working on <strong>{first}</strong>’s file, a couple of required questions. These set
            which requirements apply, so each needs a yes or no.
          </>
        }
        onCompleted={() => {
          void refetchEmployees();
          setChecklistReload((n) => n + 1);
        }}
      />

      <div className="emp-detail">
        <div className="emp-col">
          <EntityChecklistSection
            base="/api/employees"
            id={employee.id}
            title="Their requirements"
            entityNoun="employee"
            possessive={`${first}’s`}
            reloadKey={checklistReload}
          />
        </div>

        <div className="emp-col">
          <section className="acard emp-casescard">
            <div className="emp-casescard__head">
              <h2 className="acard__title">Cases involving {first}</h2>
            </div>
            {cases.length === 0 ? (
              <div className="empty">No cases involving {first}.</div>
            ) : (
              <div className="rowlist">
                {cases.map((c) => {
                  const pill = CASE_STATUS_PILL[c.status];
                  return (
                    <Link key={c.id} to={`/app/cases/${c.id}`} className="rowlist__row emp-caserow">
                      <span className="emp-caserow__main">
                        <span className="emp-caserow__title">{c.title}</span>
                        <span className="emp-caserow__meta">
                          {c.kind} · {c.opened}
                        </span>
                      </span>
                      <StatusPill variant={pill.variant}>{pill.label}</StatusPill>
                      <Chevron size={15} className="emp-caserow__chev" />
                    </Link>
                  );
                })}
              </div>
            )}
          </section>

          <p className="emp-privacy">
            <Padlock size={14} />
            <span>{first}’s record is encrypted, and only used to keep these requirements right.</span>
          </p>
        </div>
      </div>
    </div>
  );
}

export default function EmployeeDetailPage() {
  const { employees, cases, companyLoading } = useActiveCompany();
  const { employeeId } = useParams();
  const employee = employeeId ? employees.find((e) => e.id === employeeId) : undefined;

  if (!employee) {
    // Distinguish "still loading" from "genuinely not here".
    if (companyLoading) {
      return (
        <div className="apage">
          <Link to="/app/employees" className="emp-back">
            ← All employees
          </Link>
          <PageHeader eyebrow="Employee" title="Loading…" />
          <div className="acard">
            <div className="empty">Loading this person’s file…</div>
          </div>
        </div>
      );
    }
    return (
      <div className="apage">
        <Link to="/app/employees" className="emp-back">
          ← All employees
        </Link>
        <PageHeader eyebrow="Employees" title="We can’t find that person" />
        <div className="acard">
          <div className="empty">
            <p>
              We couldn’t find this person on your team. They may have been offboarded, or you may be
              looking at the wrong company.
            </p>
            <Link to="/app/employees" className="btn btn-secondary btn-sm emp-empty__btn">
              Back to your team
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const employeeCases = cases.filter((c) => c.employeeId === employee.id);

  // Key by id so session-completed checks reset between people.
  return <EmployeeDetail key={employee.id} employee={employee} cases={employeeCases} />;
}
